import {useRoute, useRouter} from 'vue-router';
import type {Language} from '@/ts/types/component/localization.type';
import {LANGUAGE_REGEX} from '@/ts/utils/consts';
import {persistentStorage} from '@/ts/instances/persistant-storage';
import {SUPPORTED_LANGUAGES, useLang} from '@/ts/composables/stateful/use-lang';
import {VoucherCodeQuery, getLanguageForVoucherCode} from '@/ts/types/component/sign-up-type';
import {detectBrowserLanguage} from '@/ts/utils/browser-language';

/**
 * Extracts language code from URL path
 * @param path - URL path to analyze (e.g., "/en/explore" or window.location.pathname)
 * @returns Language if valid language found in path, null otherwise
 */
export function extractLanguageFromPath(path: string): Language | null {
  const urlLangMatch = LANGUAGE_REGEX.PATH_WITH_LANG.exec(path);
  const urlLang = urlLangMatch?.groups?.lang;

  if (urlLang && SUPPORTED_LANGUAGES.includes(urlLang as Language)) {
    return urlLang as Language;
  }
  return null;
}

/**
 * Gets current language from browser URL
 * @returns Language if valid language found in current URL, null otherwise
 */
export function getCurrentUrlLanguage(): Language | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return extractLanguageFromPath(window.location.pathname);
}

/**
 * Detects user's preferred language with fallback priority:
 * 1. Voucher code language (e.g., German for Allianz) - takes precedence over saved language
 * 2. Saved language from storage
 * 3. Browser language preferences
 * 4. The deployment's own language (`LANGUAGE`)
 * @returns Language (never null - always has fallback)
 */
export function detectUserLanguage(): Language {
  // Check for voucher code first - this should override saved language
  const voucherLang = getLanguageForVoucherCode(VoucherCodeQuery.ALLIANZ);
  if (voucherLang) {
    return voucherLang;
  }

  const saved = persistentStorage.language;
  if (saved && SUPPORTED_LANGUAGES.includes(saved)) {
    return saved;
  }

  return detectBrowserLanguage();
}

/** Interface for switching the route's language prefix */
export interface UseI18nRouter {
  updateRouteLanguage(newLang: Language): Promise<void>;
}

/** Provides a method to switch the current route's language prefix while preserving path and query */
export function useI18nRouter(): UseI18nRouter {
  const route = useRoute();
  const router = useRouter();
  const {setLanguage} = useLang();


  const getLocalizedPath = (path: string, lang: Language): string => {
    // If the path already starts with a language code, replace it
    if (LANGUAGE_REGEX.STARTS_WITH_LANG.exec(path)) {
      return path?.replace(LANGUAGE_REGEX.REMOVE_LANG_PREFIX, `/${lang}`) ?? `/${lang}`;
    }

    if (path === '/') {
      return `/${lang}`;
    }

    return `/${lang}${path}`;
  };


  const updateRouteLanguage = async(newLang: Language): Promise<void> => {
    // Update the language setting
    setLanguage(newLang);

    // Get current path without language
    const currentPath = route.path;
    const pathWithoutLang = currentPath.replace(LANGUAGE_REGEX.REMOVE_LANG_PREFIX, '') || '/';

    // Navigate to the same path with new language
    const newPath = getLocalizedPath(pathWithoutLang, newLang);

    await router.replace({
      path: newPath,
      query: route.query,
    });
  };

  return {
    updateRouteLanguage,
  };
}
